import { useUser } from "@clerk/clerk-expo";
import { isClerkProvider } from "@helpers/auth-provider";
import type { User } from "@supabase/supabase-js";
import { getSupabaseAuthClient } from "libs/supabase-auth";
import { useEffect, useState } from "react";

type AuthUserResult = {
	email?: string;
	username: string;
	userId?: string;
	isLoaded: boolean;
	isSignedIn?: boolean;
	userPublicMetadataId?: number;
	userLogicalId?: string;
	isAdmin: boolean;
};

const useSupabaseUser = (): AuthUserResult => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [user, setUser] = useState<User | undefined>(undefined);

	useEffect(() => {
		let mounted = true;
		const loadUser = async () => {
			try {
				const supabase = getSupabaseAuthClient();
				const {
					data: { session },
				} = await supabase.auth.getSession();
				if (!mounted) {
					return;
				}

				setUser(session?.user);
			} catch {
				setUser(undefined);
			} finally {
				if (mounted) {
					setIsLoaded(true);
				}
			}
		};
		loadUser();

		let unsubscribe: (() => void) | undefined;
		try {
			const supabase = getSupabaseAuthClient();
			const {
				data: { subscription },
			} = supabase.auth.onAuthStateChange(
				(_event: string, session: { user: User | null } | null) => {
					if (!mounted) {
						return;
					}
					if (session?.user) {
						setUser(session.user);
					} else {
						setUser(undefined);
					}
					setIsLoaded(true);
				},
			);
			unsubscribe = () => subscription.unsubscribe();
		} catch {
			// Supabase client not available during initial config load
		}

		return () => {
			mounted = false;
			unsubscribe?.();
		};
	}, []);

	const email = user?.email;
	const userId = user?.id;
	const isAdmin = user?.app_metadata?.isAdmin === true;
	const userPublicMetadataId = user?.user_metadata?.numericId;
	return {
		email,
		username: (email ?? "").split("@")[0],
		userId,
		isLoaded,
		isSignedIn: !!user,
		userPublicMetadataId,
		userLogicalId: userId,
		isAdmin,
	};
};

const useClerkUserAdapter = (): AuthUserResult => {
	const { user, isLoaded, isSignedIn } = useUser();

	const email = user?.emailAddresses[0]?.emailAddress;
	const username = user?.username;
	const userId = user?.id;
	const userPublicMetadataId = user?.publicMetadata?.id as number | undefined;
	const userLogicalId = user?.publicMetadata?.logicalId as string | undefined;
	const isAdmin = user?.publicMetadata?.isAdmin === true;

	return {
		email,
		username: username || "",
		userId,
		isLoaded,
		isSignedIn,
		userPublicMetadataId,
		userLogicalId,
		isAdmin,
	};
};

// Safe at module load — provider is resolved from bundled config (build-time), not fetched remotely
const useAuthUser: () => AuthUserResult = isClerkProvider()
	? useClerkUserAdapter
	: useSupabaseUser;

export default useAuthUser;
